import React from 'react';
import { Routes, Route, Link, useLocation, useParams, Navigate } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { content } from './data/content';
import { Menu, X, Phone, Mail, MapPin, Instagram, Facebook, ChevronRight, Star, FileText } from 'lucide-react';
import { motion, AnimatePresence, useInView, animate } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import BeforeAfterSlider from './components/BeforeAfterSlider';
import ScrollToTop from './components/ScrollToTop';
import SEO from './components/SEO';
import { beforeAfterData } from './data/beforeAfter';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Components
const Navbar = () => {
  const { lang, toggleLang } = useLanguage();
  const t = content[lang].nav;
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();

  const navLinks = [
    { name: t.home, path: '/' },
    { name: t.treatments, path: '/behandlinger' },
    { name: t.priceList || (lang === 'da' ? 'Prisliste' : 'Price List'), path: '/prisliste' },
    { name: t.gallery, path: '/galleri' },
    { name: t.privateDoctor, path: '/privatlaege' },
    { name: t.academy || (lang === 'da' ? 'Akademi' : 'Academy'), path: '/akademi' },
    { name: t.contact, path: '/kontakt' },
  ];

  return (
    <nav className="sticky top-0 w-full z-50 bg-white border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-6 h-20 md:h-auto lg:h-24 flex flex-row md:flex-col lg:flex-row items-center md:items-start lg:items-center justify-between md:py-6 lg:py-0">
        <div className="flex items-center justify-between w-full lg:w-auto md:mb-6 lg:mb-0">
          <Link to="/" className="flex items-center">
            <img 
              src="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/b9f6398a-80fc-4887-83bc-1459eb7511b0.png" 
              alt="Rejuvenate Logo" 
              className="h-16 md:h-20 lg:h-22 w-auto object-contain"
            />
          </Link>

          {/* Mobile Toggle */}
          <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Desktop/Tablet Nav */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                "text-xs uppercase tracking-widest font-medium transition-colors hover:text-orange-400",
                location.pathname === link.path ? "text-orange-500" : "text-stone-600"
              )}
            >
              {link.name}
            </Link>
          ))}
          <button
            onClick={toggleLang}
            className="text-xs uppercase tracking-widest font-bold text-stone-400 hover:text-stone-900 transition-colors"
          >
            {lang === 'da' ? 'EN' : 'DA'}
          </button>
          <a
            href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-stone-900 text-white text-xs uppercase tracking-widest px-5 py-2.5 rounded-full hover:bg-stone-700 transition-colors"
          >
            {t.book}
          </a>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-stone-100 overflow-hidden"
          >
            <div className="flex flex-col gap-4 p-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="text-sm uppercase tracking-widest font-medium text-stone-600"
                >
                  {link.name}
                </Link>
              ))}
              <button onClick={toggleLang} className="text-left text-sm uppercase tracking-widest font-bold text-stone-400">
                {lang === 'da' ? 'Switch to English' : 'Skift til Dansk'}
              </button>
              <a
                href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="bg-stone-900 text-white text-center py-4 rounded-xl"
              >
                {t.book}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const Footer = () => {
  const { lang } = useLanguage();
  const t = content[lang].footer;
  const nav = content[lang].nav;
  const brand = content[lang].brandName;

  const pdfLinks = [
    {
      name: lang === 'da' ? 'Botox' : 'Botox',
      url: 'https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/2875eae5-81e4-4bec-a411-ee0258c6a2e2.pdf'
    },
    {
      name: lang === 'da' ? 'Polynukleotider' : 'Polynucleotides',
      url: 'https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/2ea099eb-f479-4cfd-b556-737eedd5aca0.pdf'
    },
    {
      name: lang === 'da' ? 'Microneedling (Skinpen)' : 'Microneedling (Skinpen)',
      url: 'https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/b1b257f3-2d0d-4752-bb03-5e828cd72cb4.pdf'
    },
    {
      name: lang === 'da' ? 'Hårfjerning (Diode laser)' : 'Hair Removal (Diode Laser)',
      url: 'https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/42d8100b-71c9-4d25-8268-e5165de67a7a.pdf'
    },
    {
      name: lang === 'da' ? 'Filler & Skinbooster' : 'Filler & Skinbooster',
      url: 'https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/15557a60-792f-48f2-b1b8-1b66ba5ee0f2.pdf'
    },
    {
      name: lang === 'da' ? 'PRF / PRP Patientinformation' : 'PRF / PRP Patient Information',
      url: 'https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/prf-prp-patientinformation.pdf'
    }
  ];

  return (
    <footer className="bg-stone-50 pt-12 pb-8 border-t border-stone-100">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
        <div className="space-y-6 lg:col-span-1">
          <Link to="/" className="inline-block">
            <img 
              src="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/b9f6398a-80fc-4887-83bc-1459eb7511b0.png" 
              alt="Rejuvenate Logo" 
              className="h-28 w-auto object-contain"
            />
          </Link>
          <p className="text-stone-500 text-sm leading-relaxed max-w-xs">
            Eksklusiv kosmetisk klinik og privatlæge med fokus på naturlig skønhed og din sundhed.
          </p>
          <div className="flex gap-4">
            <Facebook className="w-5 h-5 text-stone-900 hover:text-orange-400 cursor-pointer transition-colors" />
            <a href="https://www.instagram.com/rejuvenate_medical_clinic" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram className="w-5 h-5 text-stone-900 hover:text-orange-400 cursor-pointer transition-colors" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-widest font-bold mb-8">{lang === 'da' ? 'Kontakt' : 'Contact'}</h4>
          <ul className="space-y-4 text-sm text-stone-600 mb-6">
            <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-stone-900 shrink-0" /> {t.address}</li>
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-stone-900 shrink-0" /> {t.phone}</li>
            <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-stone-900 shrink-0" /> {t.email}</li>
          </ul>
          <div className="pt-6 border-t border-stone-200/60 space-y-3">
            <h5 className="text-xs uppercase tracking-widest font-bold text-stone-900">{lang === 'da' ? 'Åbningstider' : 'Hours'}</h5>
            <p className="text-sm text-stone-600">
              {lang === 'da' ? 'Mandag - Søndag: Efter aftale' : 'Monday - Sunday: By appointment'}
            </p>
            <p className="text-stone-500 text-xs italic leading-relaxed" data-dev-id="src/App.jsx:231:10">
              {lang === 'da' ? (
                <>
                  <a 
                    href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline hover:text-orange-500 transition-colors"
                  >
                    Book online
                  </a>{' '}
                  eller kontakt Rejuvenate på{' '}
                  <a 
                    href="https://www.instagram.com/rejuvenate_medical_clinic" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline hover:text-orange-500 transition-colors"
                  >
                    instagram
                  </a>{' '}
                  for en tid.
                </>
              ) : (
                <>
                  <a 
                    href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline hover:text-orange-500 transition-colors"
                  >
                    Book online
                  </a>{' '}
                  or contact Rejuvenate on{' '}
                  <a 
                    href="https://www.instagram.com/rejuvenate_medical_clinic" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline hover:text-orange-500 transition-colors"
                  >
                    Instagram
                  </a>{' '}
                  for an appointment.
                </>
              )}
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-widest font-bold mb-8">{lang === 'da' ? 'Genveje' : 'Quick Links'}</h4>
          <ul className="space-y-4 text-sm text-stone-600">
            <li><Link to="/behandlinger" className="hover:text-orange-500 transition-colors">{nav.treatments}</Link></li>
            <li><Link to="/prisliste" className="hover:text-orange-500 transition-colors">{nav.priceList || (lang === 'da' ? 'Prisliste' : 'Price List')}</Link></li>
            <li><Link to="/galleri" className="hover:text-orange-500 transition-colors">{nav.gallery}</Link></li>
            <li><Link to="/privatlaege" className="hover:text-orange-500 transition-colors">{nav.privateDoctor}</Link></li>
            <li><Link to="/akademi" className="hover:text-orange-500 transition-colors">{nav.academy || (lang === 'da' ? 'Akademi' : 'Academy')}</Link></li>
            <li>
              <a 
                href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-orange-500 transition-colors"
              >
                {nav.book}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-widest font-bold mb-8">{lang === 'da' ? 'Patientinformation' : 'Patient Information'}</h4>
          <ul className="space-y-4 text-sm text-stone-600">
            {pdfLinks.map((pdf, idx) => (
              <li key={idx}>
                <a 
                  href={pdf.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2.5 hover:text-orange-500 transition-colors group"
                >
                  <FileText className="w-4 h-4 text-stone-400 group-hover:text-orange-500 transition-colors shrink-0" />
                  <span>{pdf.name}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 pt-12 border-t border-stone-200 flex flex-col md:flex-row justify-between items-center gap-6 text-stone-400 text-xs uppercase tracking-widest">
        <p>&copy; {new Date().getFullYear()} {brand}. All rights reserved.</p>
        <div className="flex gap-8">
          <a 
            href="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/tilsynsrapport.pdf" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-stone-900 transition-colors"
          >
            {lang === 'da' ? 'Tilsynsrapport (PDF)' : 'Inspection Report (PDF)'}
          </a>
        </div>
      </div>
    </footer>
  );
};

const StatCounter = ({ value }) => {
  const numericString = value.replace(/[^0-9]/g, '');
  const numericValue = parseInt(numericString, 10) || 0;
  const [count, setCount] = React.useState(numericValue);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const suffix = value.replace(/[0-9.,]/g, '');
  const isThousands = value.includes('.') || value.includes(',');

  React.useEffect(() => {
    if (isInView) {
      setCount(0);
      const controls = animate(0, numericValue, {
        duration: 2,
        ease: "easeOut",
        onUpdate: (latest) => setCount(Math.floor(latest)),
      });
      return () => controls.stop();
    }
  }, [isInView, numericValue]);

  return (
    <span ref={ref}>
      {isThousands ? count.toLocaleString('da-DK') : count}
      {suffix}
    </span>
  );
};

// Pages
const Home = () => {
  const { lang } = useLanguage();
  const t = content[lang];
  const isDa = lang === 'da';

  const seoTitle = isDa 
    ? "Rejuvenate | Kosmetisk Klinik i Rødovre & København" 
    : "Rejuvenate | Cosmetic Clinic in Copenhagen & Rødovre";
  const seoDesc = isDa 
    ? "Få naturlige og smukke resultater hos Rejuvenate. Vi forener lægefaglig præcision med æstetisk sans. Botox, filler & laserhårfjerning. Book tid online!" 
    : "Achieve natural and beautiful results at Rejuvenate. We combine medical precision with aesthetic expertise. Botox, filler & laser. Book your consultation!";

  return (
    <div>
      <SEO title={seoTitle} description={seoDesc} />
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-stone-900">
        <div className="absolute inset-0 bg-stone-900/20 z-10" />
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/a1a23ead-9e24-4a1d-ae4f-31ccfb9f7994.jpg"
          className="absolute inset-0 w-full h-full object-cover object-[center_30%] scale-105"
        >
          <source src="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/51476b75-5b9e-4647-acac-b336fba8ff65.mp4" type="video/mp4" />
        </video>
        <div className="relative z-20 text-center max-w-4xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-white text-xs uppercase tracking-[0.3em] font-medium mb-6 block">
              Kosmetisk Klinik & Privatlæge
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-white mb-8 leading-tight">
              {t.hero.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-12 max-w-2xl mx-auto font-light leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a
                href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-stone-900 px-10 py-5 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-orange-50 transition-all transform hover:scale-105"
              >
                {t.hero.cta}
              </a>
              <Link
                to="/behandlinger"
                className="text-white flex items-center gap-2 group text-xs uppercase tracking-widest font-bold"
              >
                {t.hero.secondaryCta}
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
        className="py-12 bg-stone-50 border-y border-stone-100"
      >
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8">
          {t.stats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <div className="text-5xl md:text-7xl font-serif text-orange-400 mb-4 tabular-nums">
                <StatCounter value={stat.value} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Treatments Preview */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
        className="py-16 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-serif mb-6">{t.treatments.title}</h2>
            <p className="text-stone-500 max-w-xl mx-auto">{t.treatments.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.treatments.items.slice(0, 3).map((item) => (
              <div key={item.id} className="group">
                <Link to={`/behandlinger/${item.id}`} className="block relative aspect-[4/5] overflow-hidden rounded-2xl mb-6">
                  {item.image.endsWith('.mp4') || item.image.endsWith('.mov') ? (
                    item.id === 'laser' ? (
                      <div className="relative w-full h-full overflow-hidden">
                        {/* Sharp base video */}
                        <video
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        >
                          <source src={item.image} type="video/mp4" />
                        </video>
                        {/* Blurred overlay video on edges */}
                        <video
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="absolute inset-0 w-full h-full object-cover blur-[6px] pointer-events-none transition-transform duration-700 group-hover:scale-110"
                          style={{
                            maskImage: 'radial-gradient(circle, transparent 30%, black 70%)',
                            WebkitMaskImage: 'radial-gradient(circle, transparent 30%, black 70%)'
                          }}
                        >
                          <source src={item.image} type="video/mp4" />
                        </video>
                      </div>
                    ) : (
                      <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      >
                        <source src={item.image} type={item.image.endsWith('.mov') ? 'video/quicktime' : 'video/mp4'} />
                      </video>
                    )
                  ) : (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-stone-900/10 group-hover:bg-stone-900/30 transition-colors" />
                </Link>
                <h3 className="text-2xl font-serif mb-3">{item.title}</h3>
                <p className="text-stone-500 text-sm mb-6 leading-relaxed">{item.description}</p>
                <Link to={`/behandlinger/${item.id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-stone-900 group-hover:text-orange-500 transition-colors">
                  {lang === 'da' ? 'Læs mere' : 'Read more'} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Gazelle Section */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
        className="pt-12 pb-16 bg-stone-50 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1 relative aspect-[4/5] rounded-[40px] overflow-hidden shadow-2xl group md:max-w-md md:mx-auto lg:max-w-none lg:mx-0">
              <img 
                src="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/55ccec11-dbb6-4ef4-842e-cd178c2e581a.jpeg" 
                alt="Gazelle Pris" 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 transform-gpu will-change-transform [backface-visibility:hidden]"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-orange-400 text-xs uppercase tracking-widest font-bold mb-6 block">
                {lang === 'da' ? 'Mød din læge' : 'Meet your doctor'}
              </span>
              <h2 className="text-4xl md:text-5xl font-serif mb-8 leading-tight">
                Gazelle Doroudian
              </h2>
              <div className="text-stone-500 text-lg leading-relaxed mb-10 space-y-4">
                <p>
                  {lang === 'da' 
                    ? 'Speciallæge i almen medicin og stifter af Rejuvenate. Med 14 års erfaring sikrer Gazelle dig en tryg behandling med naturlige resultater.' 
                    : 'Specialist in general medicine and founder of Rejuvenate. With 14 years of experience, Gazelle ensures safe treatments with natural results.'}
                </p>
                <p>
                  {lang === 'da' 
                    ? 'Nye patienter møder altid Gazelle til en personlig konsultation før deres første behandling.' 
                    : 'New patients always meet Gazelle for a personal consultation before their first treatment.'}
                </p>
              </div>
              <a 
                href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-4 text-xs uppercase tracking-widest font-bold text-stone-900 group"
              >
                {lang === 'da' ? 'Book din tid i dag' : 'Book your appointment today'}
                <div className="w-12 h-[1px] bg-stone-300 group-hover:w-16 group-hover:bg-orange-400 transition-all" />
              </a>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Social Proof */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
        className="py-12 md:py-16 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-10 md:mb-20 gap-8">
            <div className="max-w-xl">
              <h2 className="text-4xl md:text-5xl font-serif mb-6">{t.reviews.title}</h2>
              <div className="flex items-center gap-1 text-emerald-500 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
              </div>
            </div>
            <a 
              href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-stone-900 text-white px-8 py-4 rounded-full text-xs uppercase tracking-widest font-bold text-center"
            >
              {lang === 'da' ? 'Book din tid i dag' : 'Book your appointment today'}
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.reviews.items.map((review, idx) => (
              <div key={idx} className="bg-white p-6 md:p-10 rounded-3xl shadow-sm border border-stone-100 md:border-0">
                <p className="text-stone-600 italic mb-6 md:mb-8 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center gap-4">
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider">{review.name}</div>
                    <div className="text-xs text-stone-400">Verificeret kunde</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Before & After Preview */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
        className="py-12 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-400 text-xs uppercase tracking-widest font-bold mb-6 block">
              {lang === 'da' ? 'Dokumenterede Resultater' : 'Proven Results'}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif mb-8">
              {lang === 'da' ? 'Se forvandlingen' : 'Experience the Transformation'}
            </h2>
            <p className="text-stone-500 max-w-2xl mx-auto">
              {lang === 'da' 
                ? 'Vi lægger vægt på naturlige resultater, der fremhæver dine egne træk. Træk i slideren for at se før- og efter-billeder af vores behandlinger.' 
                : 'We focus on natural results that enhance your own features. Drag the slider to see before and after photos of our treatments.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {beforeAfterData.slice(0, 2).map((item) => (
              <div key={item.id} className="space-y-6">
                <BeforeAfterSlider 
                  beforeImage={item.before}
                  afterImage={item.after}
                  beforeLabel={item.hideLabels ? "" : (lang === 'da' ? 'Før' : 'Before')}
                  afterLabel={item.hideLabels ? "" : (lang === 'da' ? 'Efter' : 'After')}
                />
                <div className="text-center">
                  <h3 className="text-xl font-serif">{item.title[lang]}</h3>
                  <p className="text-stone-400 text-xs uppercase tracking-widest mt-2">{item.description[lang]}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to="/galleri" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-stone-900 hover:text-orange-500 transition-colors">
              {lang === 'da' ? 'Se hele galleriet' : 'View full gallery'} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

const Treatments = () => {
  const { lang } = useLanguage();
  const t = content[lang].treatments;
  const isDa = lang === 'da';

  const seoTitle = isDa 
    ? "Professionelle Skønhedsbehandlinger | Rejuvenate" 
    : "Professional Cosmetic Treatments | Rejuvenate";
  const seoDesc = isDa 
    ? "Se vores udvalg af skræddersyede behandlinger: Botox, filler, skinbooster, laserhårfjerning og microneedling. Skab dit smukkeste jeg. Book tid i dag!" 
    : "Explore our tailored treatments: Botox, filler, skinbooster, laser hair removal, and microneedling. Reveal your most beautiful self. Book online now!";

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="pt-16 md:pt-24 pb-24"
    >
      <SEO title={seoTitle} description={seoDesc} />
      <div className="max-w-7xl mx-auto px-6">
        <header className="mb-20">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-5xl font-serif mb-6"
          >
            {t.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-stone-500 max-w-2xl"
          >
            {t.subtitle}
          </motion.p>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {t.items.map((item, idx) => (
            <motion.div 
              key={item.id} 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              className="bg-white border border-stone-100 rounded-3xl overflow-hidden hover:shadow-xl transition-shadow group"
            >
              <Link to={`/behandlinger/${item.id}`} className="block aspect-video overflow-hidden">
                {item.image.endsWith('.mp4') || item.image.endsWith('.mov') ? (
                  item.id === 'laser' ? (
                    <div className="relative w-full h-full overflow-hidden">
                      {/* Sharp base video */}
                      <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      >
                        <source src={item.image} type="video/mp4" />
                      </video>
                      {/* Blurred overlay video on edges */}
                      <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover blur-[6px] pointer-events-none transition-transform group-hover:scale-105"
                        style={{
                          maskImage: 'radial-gradient(circle, transparent 30%, black 70%)',
                          WebkitMaskImage: 'radial-gradient(circle, transparent 30%, black 70%)'
                        }}
                      >
                        <source src={item.image} type="video/mp4" />
                      </video>
                    </div>
                  ) : (
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    >
                      <source src={item.image} type={item.image.endsWith('.mov') ? 'video/quicktime' : 'video/mp4'} />
                    </video>
                  )
                ) : (
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                )}
              </Link>
              <div className="p-8">
                <h3 className="text-2xl font-serif mb-4">{item.title}</h3>
                <p className="text-stone-500 mb-8 text-sm leading-relaxed">{item.description}</p>
                <Link to={`/behandlinger/${item.id}`} className="block w-full text-center bg-stone-50 hover:bg-stone-900 hover:text-white transition-colors py-4 rounded-xl text-xs uppercase tracking-widest font-bold">
                  {lang === 'da' ? 'Læs mere' : 'Read more'}
                </Link>
              </div>
            </motion.div>
          ))}
          {/* Private Doctor Card as a special treatment item if not in list */}
          {!t.items.find(i => i.id === 'private-doctor') && (
             <div className="bg-stone-900 text-white rounded-3xl overflow-hidden hover:shadow-xl transition-shadow group p-8 flex flex-col justify-between">
                <div>
                  <h3 className="text-2xl font-serif mb-4">{lang === 'da' ? 'Privatlæge' : 'Private Doctor'}</h3>
                  <p className="text-stone-400 mb-8 text-sm leading-relaxed">
                    {lang === 'da' ? 'Personlig lægehjælp uden ventetid.' : 'Personal medical care without waiting time.'}
                  </p>
                </div>
                <Link to="/privatlaege" className="block w-full text-center bg-white/10 hover:bg-white text-white hover:text-stone-900 transition-colors py-4 rounded-xl text-xs uppercase tracking-widest font-bold">
                  {lang === 'da' ? 'Se mere' : 'See more'}
                </Link>
             </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const TreatmentDetail = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const t = content[lang];
  const treatment = t.treatments.items.find(item => item.id === id);

  if (!treatment) {
    return <Navigate to="/behandlinger" />;
  }

  const isDa = lang === 'da';

  const seoData = {
    botox: {
      da: {
        title: "Botox Behandling i Rødovre – Få et friskere look | Rejuvenate",
        desc: "Effektiv reduktion af rynker og fine linjer med Botox hos Rejuvenate. Naturlige og smukke resultater med lægefaglig præcision. Book din tid online!"
      },
      en: {
        title: "Botox Treatment in Copenhagen – Refresh Your Look | Rejuvenate",
        desc: "Effective reduction of wrinkles and fine lines with Botox at Rejuvenate. Natural, beautiful results with medical precision. Book your consultation today!"
      }
    },
    filler: {
      da: {
        title: "Filler Behandling i Rødovre – Naturlig volumen | Rejuvenate",
        desc: "Genskab volumen og definer dine ansigtstræk naturligt med ikke-permanente fillere hos Rejuvenate. Professionel lægefaglig ekspertise. Book tid online!"
      },
      en: {
        title: "Dermal Filler Treatment – Restore Natural Volume | Rejuvenate",
        desc: "Restore lost volume and define your facial features naturally with dermal fillers at Rejuvenate. Safe procedures and medical expertise. Book online now!"
      }
    },
    laser: {
      da: {
        title: "Permanent Laserhårfjerning i Rødovre | Rejuvenate",
        desc: "Slip for barbering og indgroede hår. Permanent hårreduktion med den nyeste og mest effektive laserteknologi hos Rejuvenate. Smertefrit. Book tid nu!"
      },
      en: {
        title: "Permanent Laser Hair Removal Copenhagen | Rejuvenate",
        desc: "Say goodbye to shaving and ingrown hairs. Permanent hair reduction with advanced laser technology at Rejuvenate. Painless & safe. Book your session now!"
      }
    },
    skinbooster: {
      da: {
        title: "Skinbooster Behandling – Intensiv fugt & glød | Rejuvenate",
        desc: "Få dybdegående fugt, forbedret hudkvalitet og øjeblikkelig glød med Skinbooster hos Rejuvenate. Perfekt til tør eller træt hud. Book din tid online!"
      },
      en: {
        title: "Skinbooster Treatment – Deep Hydration & Glow | Rejuvenate",
        desc: "Get deep hydration, improved skin structure, and an instant radiant glow with Skinbooster at Rejuvenate. Perfect for tired skin. Book your appointment!"
      }
    },
    microneedling: {
      da: {
        title: "Microneedling i Rødovre – Opstramning & kollagen | Rejuvenate",
        desc: "Stimuler hudens egen kollagenproduktion med microneedling hos Rejuvenate. Minimerer porer, ar og fine linjer for en fastere hud. Book tid online!"
      },
      en: {
        title: "Microneedling Copenhagen – Firmer & Smoother Skin | Rejuvenate",
        desc: "Stimulate your skin's collagen production with microneedling at Rejuvenate. Minimize pores, scars, and fine lines for firmer skin. Book online today!"
      }
    }
  };

  const currentSeo = seoData[id]?.[lang] || {
    title: `${treatment.title} | Rejuvenate`,
    desc: `${treatment.description} Book tid hos Rejuvenate i dag.`
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="pt-16 md:pt-24 pb-24"
    >
      <SEO title={currentSeo.title} description={currentSeo.desc} />
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-[40px] overflow-hidden shadow-2xl">
              {treatment.image.endsWith('.mp4') || treatment.image.endsWith('.mov') ? (
                treatment.id === 'laser' ? (
                  <div className="relative w-full h-full overflow-hidden">
                    {/* Sharp base video */}
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src={treatment.image} type="video/mp4" />
                    </video>
                    {/* Blurred overlay video on edges */}
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="absolute inset-0 w-full h-full object-cover blur-[6px] pointer-events-none"
                      style={{
                        maskImage: 'radial-gradient(circle, transparent 30%, black 70%)',
                        WebkitMaskImage: 'radial-gradient(circle, transparent 30%, black 70%)'
                      }}
                    >
                      <source src={treatment.image} type="video/mp4" />
                    </video>
                  </div>
                ) : (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                  >
                    <source src={treatment.image} type={treatment.image.endsWith('.mov') ? 'video/quicktime' : 'video/mp4'} />
                  </video>
                )
              ) : (
                <img src={treatment.image} alt={treatment.title} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-orange-100 rounded-full -z-10 blur-3xl opacity-50" />
          </motion.div>

          <div className="flex flex-col justify-center">
            <h1 className="text-5xl md:text-6xl font-serif mb-8">{treatment.title}</h1>
            <p className="text-stone-600 text-lg leading-relaxed mb-10">
              {treatment.longDescription}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
              <div>
                <h4 className="text-xs uppercase tracking-widest font-bold mb-6 text-stone-900">
                  {isDa ? 'Fordele' : 'Benefits'}
                </h4>
                <ul className="space-y-4">
                  {treatment.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-stone-500">
                      <div className="w-1.5 h-1.5 bg-black rounded-full mt-1.5 shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-widest font-bold mb-6 text-stone-900">
                  {isDa ? 'Selve behandlingen' : 'The process'}
                </h4>
                <p className="text-sm text-stone-500 leading-relaxed">
                  {treatment.process}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6">
              <a 
                href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-stone-900 text-white px-10 py-5 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-stone-700 transition-colors text-center"
              >
                {isDa ? 'Book tid nu' : 'Book appointment'}
              </a>
              <a 
                href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="border border-stone-200 text-stone-900 px-10 py-5 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-stone-50 transition-colors text-center"
              >
                {isDa ? 'Gratis konsultation' : 'Free consultation'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Gallery = () => {
  const { lang } = useLanguage();
  const t = content[lang].gallery;
  const isDa = lang === 'da';

  const seoTitle = isDa 
    ? "Før & Efter Billeder – Se Vores Resultater | Rejuvenate" 
    : "Before & After Gallery – See Our Results | Rejuvenate";
  const seoDesc = isDa 
    ? "Se de fantastiske, naturlige resultater af vores Botox- og fillerbehandlinger. Virkelige før/efter-billeder af glade kunder hos Rejuvenate. Book tid nu!" 
    : "Explore the amazing, natural results of our Botox and filler treatments. Real before/after photos of happy clients at Rejuvenate. Book your session!";

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="pt-16 md:pt-24 pb-24 bg-stone-50" 
    >
      <SEO title={seoTitle} description={seoDesc} />
      <div className="max-w-5xl mx-auto px-6">
        {/* Featured Interactive Sliders Grid */}
        <div>
          <div className="text-center mb-16">
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-orange-400 text-xs uppercase tracking-widest font-bold mb-4 block"
            >
              {lang === 'da' ? 'Interaktive Resultater' : 'Interactive Results'}
            </motion.span>
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl md:text-4xl font-serif mb-4"
            >
              {lang === 'da' ? 'Se forvandlingen' : 'See the transformation'}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-stone-500 text-sm max-w-lg mx-auto"
            >
              {lang === 'da' ? 'Træk i slideren for at se resultatet før og efter behandlingen.' : 'Drag the slider to see the result before and after the treatment.'}
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
            {beforeAfterData.map((item, idx) => (
              <motion.div 
                key={item.id} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (idx % 2) * 0.1, duration: 0.5 }}
                className="bg-white p-4 pb-5 rounded-[28px] shadow-sm border border-stone-100 flex flex-col"
              >
                <div className="overflow-hidden rounded-2xl">
                  {item.before ? (
                    <BeforeAfterSlider 
                      beforeImage={item.before}
                      afterImage={item.after}
                      beforeLabel={item.hideLabels ? "" : (lang === 'da' ? 'Før' : 'Before')}
                      afterLabel={item.hideLabels ? "" : (lang === 'da' ? 'Efter' : 'After')}
                    />
                  ) : (
                    <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden select-none">
                      <img
                        src={item.after}
                        alt={item.title[lang]}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                <div className="text-center mt-4">
                  <h3 className="text-lg font-serif text-stone-900">{item.title[lang]}</h3>
                  <p className="text-[11px] text-stone-400 uppercase tracking-widest mt-1">{item.description[lang]}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const PrivateDoctor = () => {
  const { lang } = useLanguage();
  const isDa = lang === 'da';

  const seoTitle = isDa 
    ? "Privatlæge Uden Ventetid i Rødovre | Rejuvenate" 
    : "Private Doctor Without Waiting Time | Rejuvenate";
  const seoDesc = isDa 
    ? "Få personlig lægehjælp, akutte konsultationer og medicinsk vægttab uden ventetid hos din private læge hos Rejuvenate. Høj faglig ekspertise. Book nu!" 
    : "Get personal medical care, acute consultations, and medical weight loss without waiting times at Rejuvenate. High professional expertise. Book online!";

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="pt-16 md:pt-24 pb-24"
    >
      <SEO title={seoTitle} description={seoDesc} />
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <span className="text-orange-400 text-xs uppercase tracking-widest font-bold mb-6 block">
              {isDa ? 'Personlig lægehjælp' : 'Personal Medical Care'}
            </span>
            <h1 className="text-5xl font-serif mb-8 leading-tight">
              {isDa ? 'Din private læge uden ventetid' : 'Your Private Doctor Without Waiting Time'}
            </h1>
            <div className="space-y-6 text-stone-500 leading-relaxed">
              <p>
                {isDa ? 'Vi tilbyder private lægekonsultationer til dig, der ønsker tid, tryghed og faglig ekspertise uden de sædvanlige ventetider.' : 'We offer private medical consultations for those who want time, security, and professional expertise without the usual waiting times.'}
              </p>
              <ul className="space-y-4 pt-6">
                {[
                  isDa ? 'Akutte konsultationer' : 'Acute consultations',
                  isDa ? 'Medicinsk vægttab' : 'Medical weight loss',
                  isDa ? 'Rejsevejledning og vaccination' : 'Travel guidance and vaccinations'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-stone-900 font-medium">
                    <ChevronRight className="w-4 h-4 text-orange-400" /> {item}
                  </li>
                ))}
              </ul>
              <div className="pt-10">
                <a 
                  href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-stone-900 text-white px-10 py-5 rounded-full text-xs uppercase tracking-widest font-bold inline-block"
                >
                  {isDa ? 'Book konsultation' : 'Book Consultation'}
                </a>
              </div>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-orange-100 rounded-[40px] -z-10 transform rotate-2" />
            <img
              src="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/55ccec11-dbb6-4ef4-842e-cd178c2e581a.jpeg"
              alt="Børsen Gazelle vinder"
              className="w-full aspect-[4/5] object-cover rounded-[32px] shadow-2xl"
            />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const Contact = () => {
  const { lang } = useLanguage();
  const isDa = lang === 'da';

  const seoTitle = isDa 
    ? "Kontakt Rejuvenate i Rødovre – Book Tid Eller Ring | Rejuvenate" 
    : "Contact Rejuvenate in Copenhagen – Book or Call Us | Rejuvenate";
  const seoDesc = isDa 
    ? "Har du spørgsmål eller ønsker du at booke en tid? Kontakt Rejuvenate i Rødovre på tlf: +4552407752 eller besøg os på Næsbyvej 6. Vi glæder os til at se dig!" 
    : "Have questions or want to book an appointment? Contact Rejuvenate in Rødovre at +4552407752 or visit us at Næsbyvej 6. We look forward to seeing you!";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        alert(isDa ? 'Tak for din henvendelse! Vi vender tilbage hurtigst muligt.' : 'Thank you for your inquiry! We will get back to you as soon as possible.');
        e.target.reset();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="pt-16 md:pt-24 pb-24 bg-stone-50"
    >
      <SEO title={seoTitle} description={seoDesc} />
      <div className="max-w-3xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="bg-white p-12 md:p-20 rounded-[40px] shadow-sm border border-stone-100"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-serif mb-6">{isDa ? 'Kontakt os' : 'Contact Us'}</h1>
            <p className="text-stone-500 mb-8">
              {isDa ? 'Har du spørgsmål eller ønsker du at booke en tid? Udfyld formularen, så kontakter vi dig.' : 'Have questions or want to book an appointment? Fill out the form and we\'ll contact you.'}
            </p>
            
            <div className="bg-stone-50 p-6 rounded-2xl inline-block text-left w-full max-w-md border border-stone-100">
              <h3 className="text-xs uppercase tracking-widest font-bold mb-4 text-stone-900">{isDa ? 'Åbningstider' : 'Opening Hours'}</h3>
              <div className="space-y-2 mb-4">
                {content[lang].footer.hours.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm text-stone-600 border-b border-stone-200/50 pb-2 last:border-0">
                    <span>{item.day}</span>
                    <span className="font-medium">{item.hours}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-stone-400 italic leading-relaxed">
                {content[lang].footer.bookingNote}
              </p>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Honeypot */}
            <div className="sr-only absolute opacity-0">
              <label htmlFor="website_url">Website URL</label>
              <input type="text" id="website_url" name="website_url" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Navn' : 'Name'}</label>
                <input required name="name" type="text" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Telefon' : 'Phone'}</label>
                <input required name="phone" type="tel" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors" />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">Email</label>
              <input required name="email" type="email" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors" />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Hvad drejer det sig om?' : 'What is it regarding?'}</label>
              <select name="service" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors bg-transparent">
                {content[lang].treatments.items.map(item => (
                  <option key={item.id} value={item.id}>{item.title}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Besked' : 'Message'}</label>
              <textarea name="message" rows="4" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors resize-none"></textarea>
            </div>

            <button type="submit" className="w-full bg-stone-900 text-white py-6 rounded-2xl text-xs uppercase tracking-widest font-bold hover:bg-stone-700 transition-colors shadow-lg">
              {isDa ? 'Send besked' : 'Send Message'}
            </button>
          </form>
        </motion.div>
      </div>
    </motion.div>
  );
};

const priceListData = {
  da: [
    {
      category: "Filler (Revolax)",
      description: "Forundersøgelse/konsultation: gratis",
      items: [
        { name: "Filler 0,5ml", price: "1.400,-" },
        { name: "Filler 1ml", price: "2.000,-" },
        { name: "Filler 2 ml", price: "3.600,-" },
        { name: "Filler 3ml", price: "4.800,-" },
        { name: "Filler 4ml", price: "6.000,-" },
        { name: "Liquid Facelift", price: "8.500,-" },
        { name: "Næsefiller", price: "2.500,-" },
        { name: "Teartrough med Teosyal Puresense Redensity II", price: "2.800,-" },
      ],
      subsections: [
        {
          title: "Opløsning af filler",
          items: [
            { name: "1. behandling", price: "1.500,-" },
            { name: "2. behandling", price: "500,-" }
          ]
        }
      ]
    },
    {
      category: "Botox (Vistabel / Dysport)",
      description: "Forundersøgelse/konsultation: gratis",
      items: [
        { name: "1 område", price: "1.200,-" },
        { name: "2 områder", price: "1.900,-" },
        { name: "3 områder", price: "2.800,-" },
        { name: "Små områder *", price: "800,-" },
        { name: "Små områder som tilvalg til minimum 1 område (gælder ikke gummy smile)", price: "400,-" },
        { name: "Tyggemuskler / kosmetisk masseterreduktion", price: "2.800,-" },
        { name: "Nose slimming", price: "1.500,-" },
        { name: "Hyperhidrose (Medicinsk behandling mod overdreven svedproduktion med botox) hænder, armhuler el. fodsåler", price: "5.000,-" },
        { name: "Nefertiti løft (kæbelinje, mundvige, hage, laterale halsbånd øverste 1/3)", price: "1.900,-" },
        { name: "Halsbånd (platysma)", price: "2.800,-" },
        { name: "Full face eksl. V-shape", price: "4.200,-" },
        { name: "Full face inkl. V-shape", price: "5.700,-" },
        { name: "Full face inkl. Halsbånd", price: "5.700,-" }
      ],
      subsections: [
        {
          title: "Touch-up (12-14 dage efter behandling)",
          items: [
            { name: "Touch-up imellem bryn", price: "50,- pr. unit" },
            { name: "Touch-up i pande udover (4 IU Vistabel eller 10 IU Dysport)", price: "50,- pr. unit" },
            { name: "Touch-up hyperhidrose (udover 8 IU Vistabel eller 20 IU Dysport)", price: "50,- pr. unit" },
            { name: "Øvrig touch-up", price: "Gratis" }
          ]
        }
      ],
      notes: [
        "* Små områder dækker over lipflip, gummy smile, nedadvendte mundvige, brynløft, bunnylines og overaktiv hage"
      ]
    },
    {
      category: "Pakketilbud botox/filler",
      description: "Kombinationsbehandlinger til reduceret pris",
      items: [
        { name: "1 område botox & 0,5ml filler", price: "2.300,- (spar 300,-)" },
        { name: "2 områder botox & 0,5 ml filler", price: "3.000,- (spar 300,-)" },
        { name: "3 områder botox & 0,5 ml filler", price: "3.800,- (spar 400,-)" },
        { name: "1 område botox & 1ml filler", price: "2.900,- (spar 300,-)" },
        { name: "2 områder botox & 1 ml filler", price: "3.500,- (spar 400,-)" },
        { name: "3 områder botox & 1 ml filler", price: "4.300,- (spar 500,-)" },
        { name: "1 område botox & 2 ml filler", price: "4.500,- (spar 300,-)" },
        { name: "2 områder botox & 2 ml filler", price: "5.100,- (spar 400,-)" },
        { name: "3 områder botox & 2ml filler", price: "6.000,- (spar 400,-)" }
      ]
    },
    {
      category: "Skinbooster",
      description: "Forundersøgelse/konsultation: gratis",
      subsections: [
        {
          title: "Ejal40 Skinbooster",
          items: [
            { name: "Ejal40 2ml", price: "1.200,-" },
            { name: "Ejal40 behandlingskur 3x2ml", price: "3.000,-" }
          ]
        },
        {
          title: "Profhilo Skinbooster",
          items: [
            { name: "Profhilo 2ml", price: "2.200,-" },
            { name: "Profhilo (3 behandlinger)", price: "5.600,-" }
          ]
        },
        {
          title: "Profhilo Structura",
          items: [
            { name: "1 behandling (1x2ml)", price: "3.500,-" },
            { name: "Behandlingskur (2x2ml)", price: "6.000,-" }
          ]
        },
        {
          title: "Kombipakke Profhilo",
          items: [
            { name: "Enkelt behandling Profhilo (2ml) & Profhilo Structura (2ml)", price: "5.100,-" }
          ]
        },
        {
          title: "Sunekos Performa",
          items: [
            { name: "Behandlingskur øjne (3x1ml)", price: "6.000,-" },
            { name: "Boost øjne (1x1ml)", price: "2.200,-" }
          ]
        },
        {
          title: "Vitaran Polynukleotider",
          items: [
            { name: "Behandlingskur øjne (3x1ml)", price: "6.000,-" },
            { name: "Behandlingskur ansigt (3x2ml)", price: "6.000,-" },
            { name: "Boost ansigt eller øjne", price: "2.200,-" }
          ]
        },
        {
          title: "Kamilane Shine",
          items: [
            { name: "Behandlingskur ansigt (3x2ml)", price: "6.000,-" },
            { name: "Kamilane shine 2ml", price: "2.200,-" }
          ]
        }
      ]
    },
    {
      category: "Microneedling & PRP",
      description: "Forundersøgelse/konsultation: gratis",
      subsections: [
        {
          title: "Skinpen Microneedling",
          items: [
            { name: "Skinpen ansigt og hals", price: "1.500,-" },
            { name: "Skinpen med Purasomes (prisvindende exosomer)", price: "2.000,-" },
            { name: "Pakkepris 3 behandlinger med Skinpen", price: "3.600,-" },
            { name: "Pakkepris 3 behandlinger med exosomer", price: "4.800,-" }
          ]
        },
        {
          title: "Pakkeaftaler Skinpen + Bio-stimulator",
          items: [
            { name: "Skinpen + Ejal40", price: "2.200,-" },
            { name: "Skinpen + Vitaran", price: "2.800,-" }
          ]
        },
        {
          title: "PRP/PRF Behandling",
          items: [
            { name: "1 Behandling, kombineret microneedling og injektion i ansigt inkl. under øjne", price: "1.800,-" },
            { name: "1 Behandling, kombineret microneedling og injektion, hovedbund (stimulerer hårvækst)", price: "1.800,-" },
            { name: "Pakkepris 3 behandlinger ansigt eller hovedbund", price: "4.320,-" }
          ]
        },
        {
          title: "EXO-GOLD",
          items: [
            { name: "1 Behandling kombineret microneedling og injektion i hovedbund med PRP og exosomer", price: "2.300,-" },
            { name: "Pakkepris 3 behandlinger", price: "6.000,-" }
          ]
        }
      ]
    },
    {
      category: "Rejuvenate pakker",
      description: "Vores mest eksklusive og komplette foryngelsespakker",
      items: [
        {
          name: "Ultimate Rejuvenation 1",
          description: "Full face botox inkl. halsbånd, 4 ml filler, 2ml Kamilane Shine, 1ml Vitaran ELLER 1ml Sunekos Performa til under øjne",
          price: "DKK 12.075,- (spar 4.025,-)"
        },
        {
          name: "Ultimate Rejuvenation 2",
          description: "Full Face botox, 3 ml filler, 2ml Kamilane Shine, 1ml Vitaran ELLER 1ml Sunekos Performa til under øjne",
          price: "DKK 10.050,- (spar 3.350,-)"
        },
        {
          name: "Maximum Rejuvenation 1",
          description: "3 områder botox inkl. Nefertiti lift, 2ml filler, 2ml Ejal 40",
          price: "DKK 7.125,- (spar 2.375,-)"
        },
        {
          name: "Maximum Rejuvenation 2",
          description: "3 områder botox, 1ml filler, 2ml Ejal40, 1ml Vitaran eller 1ml Sunekos Performa til under øjne",
          price: "DKK 6.150,- (spar 2.050,-)"
        },
        {
          name: "Medium Rejuvenation",
          description: "2 områder botox, 2ml Ejal 40, 1ml Vitaran ELLER 1 ml Sunekos Performa til under øjne",
          price: "DKK 4.240,- (spar 1.060,-)"
        },
        {
          name: "Mini Rejuvenation 1",
          description: "1 område botox, 1ml filler, 2ml Ejal40",
          price: "DKK 3.740,- (spar 660,-)"
        },
        {
          name: "Mini Rejuvenation 2",
          description: "1 område botox, 2ml Kamilane Shine ELLER 1ml Sunekos Performa ELLER 1ml Vitaran til under øjne",
          price: "DKK 2.890,- (spar 510,-)"
        },
        {
          name: "Rejuvenate Special",
          description: "1 PRF kombineret microneedling og injektion i ansigt inkl. under øjne, 2 områder botox",
          price: "DKK 3.100,- (spar 300,-)"
        }
      ]
    },
    {
      category: "Laserhårfjerning",
      description: "Permanent reduktion af uønsket hårvækst med den nyeste teknologi",
      subsections: [
        {
          title: "Kvinder",
          items: [
            { name: "FULL-BODY LASER KVINDER", price: "2.000,-" },
            { name: "Hele ben", price: "1.000,-" },
            { name: "Halve ben", price: "700,-" },
            { name: "Hals", price: "400,-" },
            { name: "Hele ansigt", price: "400,-" },
            { name: "Overlæbe", price: "200,-" },
            { name: "Hage", price: "200,-" },
            { name: "Armhuler", price: "400,-" },
            { name: "Hele arme", price: "1.000,-" },
            { name: "Halve arme", price: "700,-" },
            { name: "Bryst", price: "700,-" },
            { name: "Mave", price: "700,-" },
            { name: "Brazilian", price: "800,-" },
            { name: "Bikinilinje", price: "400,-" },
            { name: "Numse (mellem baller)", price: "400,-" },
            { name: "Baller", price: "600,-" },
            { name: "Fødder", price: "200,-" },
            { name: "Skuldre", price: "700,-" },
            { name: "Hel Ryg", price: "1.200,-" },
            { name: "Lænd", price: "600,-" },
            { name: "Nakke", price: "400,-" }
          ]
        },
        {
          title: "Pakketilbud Mænd",
          items: [
            { name: "Maximum Package (Hel overkrop)", price: "2.000,-" },
            { name: "Medium Package 1 (Ryg, skuldre, overarme og armhule)", price: "1.500,-" },
            { name: "Medium Package 2 (Brystkasse, Mave, Ryg)", price: "1.500,-" },
            { name: "Mini Package 1 (Ryg og skuldre)", price: "1.000,-" },
            { name: "Mini Package 2 (brystkasse og mave)", price: "1.000,-" },
            { name: "Facial package (Kinder, hals og nakke)", price: "800,-" }
          ]
        }
      ]
    },
    {
      category: "Privatlæge & Vægttab",
      description: "Personlig lægehjælp og medicinsk vægttab",
      subsections: [
        {
          title: "Medicinsk Vægttab",
          items: [
            { name: "Forundersøgelse, ONLINE, inkl. recept (medicin hentes og betales særskilt på apotek)", price: "600,-" },
            { name: "Opfølgning, ONLINE, inkl. recept (medicin hentes og betales særskilt på apotek)", price: "300,-" },
            { name: "Off-label recept *", price: "800,-" }
          ],
          notes: [
            "* BMI < 30 eller BMI fra 27 og fravær af livsstilssygdomme. BMI < 25 behandles ikke!"
          ]
        },
        {
          title: "Privat læge konsultation og lægevagts funktion",
          items: [
            { name: "Konsultation, online", price: "600,-" },
            { name: "Konsultation, fremmøde", price: "800,- inkl. services" },
            { name: "Attest, sygdom arbejde", price: "1.200,-" },
            { name: "Attest, sygdom studie", price: "600,-" },
            { name: "Helbredsattest, FRA", price: "500,- inkl. services" }
          ],
          notes: [
            "Målgruppe: Voksne og børn fra 0-18 år.",
            "Henvendelsesårsager: Akutte luftvejs,- eller urinvejsgener, akutte mavesmerter, underlivsgener, akutte hududslæt, hudsygdomme (akne, eksem, psoriasis, rosacea, melasma, sebboroisk dermatitis, perioral dermatitis), gynækologi (prævention, hormonbehandling for kvinder i overgangsalderen, oplægning/fjernelse af spiral, indsættelse/fjernelse af P-stav)."
          ]
        }
      ]
    }
  ],
  en: [
    {
      category: "Filler (Revolax)",
      description: "Consultation/pre-examination: free",
      items: [
        { name: "Filler 0.5ml", price: "1,400,-" },
        { name: "Filler 1ml", price: "2,000,-" },
        { name: "Filler 2 ml", price: "3,600,-" },
        { name: "Filler 3ml", price: "4,800,-" },
        { name: "Filler 4ml", price: "6,000,-" },
        { name: "Liquid Facelift", price: "8,500,-" },
        { name: "Nose Filler", price: "2,500,-" },
        { name: "Teartrough with Teosyal Puresense Redensity II", price: "2,800,-" },
      ],
      subsections: [
        {
          title: "Dissolving filler",
          items: [
            { name: "1st treatment", price: "1,500,-" },
            { name: "2nd treatment", price: "500,-" }
          ]
        }
      ]
    },
    {
      category: "Botox (Vistabel / Dysport)",
      description: "Consultation/pre-examination: free",
      items: [
        { name: "1 area", price: "1,200,-" },
        { name: "2 areas", price: "1,900,-" },
        { name: "3 areas", price: "2,800,-" },
        { name: "Small areas *", price: "800,-" },
        { name: "Small areas as add-on to minimum 1 area (excludes gummy smile)", price: "400,-" },
        { name: "Masseter / cosmetic jaw reduction", price: "2,800,-" },
        { name: "Nose slimming", price: "1,500,-" },
        { name: "Hyperhidrosis (Medical treatment for excessive sweating with botox) hands, underarms, or soles of feet", price: "5,000,-" },
        { name: "Nefertiti lift (jawline, mouth corners, chin, lateral neck bands upper 1/3)", price: "1,900,-" },
        { name: "Neck bands (platysma)", price: "2,800,-" },
        { name: "Full face excl. V-shape", price: "4,200,-" },
        { name: "Full face incl. V-shape", price: "5,700,-" },
        { name: "Full face incl. Neck bands", price: "5,700,-" }
      ],
      subsections: [
        {
          title: "Touch-up (12-14 days after treatment)",
          items: [
            { name: "Touch-up between brows", price: "50,- per unit" },
            { name: "Touch-up in forehead beyond (4 IU Vistabel or 10 IU Dysport)", price: "50,- per unit" },
            { name: "Touch-up hyperhidrosis beyond (8 IU Vistabel or 20 IU Dysport)", price: "50,- per unit" },
            { name: "Other touch-up", price: "Free" }
          ]
        }
      ],
      notes: [
        "* Small areas cover lip flip, gummy smile, downturned mouth corners, brow lift, bunny lines, and overactive chin"
      ]
    },
    {
      category: "Package Deals Botox/Filler",
      description: "Combined treatments at a reduced price",
      items: [
        { name: "1 area botox & 0.5ml filler", price: "2,300,- (save 300,-)" },
        { name: "2 areas botox & 0.5 ml filler", price: "3,000,- (save 300,-)" },
        { name: "3 areas botox & 0.5 ml filler", price: "3,800,- (save 400,-)" },
        { name: "1 area botox & 1ml filler", price: "2,900,- (save 300,-)" },
        { name: "2 areas botox & 1 ml filler", price: "3,500,- (save 400,-)" },
        { name: "3 areas botox & 1 ml filler", price: "4,300,- (save 500,-)" },
        { name: "1 area botox & 2 ml filler", price: "4,500,- (save 300,-)" },
        { name: "2 areas botox & 2 ml filler", price: "5,100,- (save 400,-)" },
        { name: "3 areas botox & 2ml filler", price: "6,000,- (save 400,-)" }
      ]
    },
    {
      category: "Skinbooster",
      description: "Consultation/pre-examination: free",
      subsections: [
        {
          title: "Ejal40 Skinbooster",
          items: [
            { name: "Ejal40 2ml", price: "1,200,-" },
            { name: "Ejal40 treatment course 3x2ml", price: "3,000,-" }
          ]
        },
        {
          title: "Profhilo Skinbooster",
          items: [
            { name: "Profhilo 2ml", price: "2,200,-" },
            { name: "Profhilo (3 treatments)", price: "5,600,-" }
          ]
        },
        {
          title: "Profhilo Structura",
          items: [
            { name: "1 Treatment (1x2ml)", price: "3,500,-" },
            { name: "Treatment course (2x2ml)", price: "6,000,-" }
          ]
        },
        {
          title: "Kombipakke Profhilo",
          items: [
            { name: "Single treatment Profhilo (2ml) & Profhilo Structura (2ml)", price: "5,100,-" }
          ]
        },
        {
          title: "Sunekos Performa",
          items: [
            { name: "Treatment course eyes (3x1ml)", price: "6,000,-" },
            { name: "Boost eyes (1x1ml)", price: "2,200,-" }
          ]
        },
        {
          title: "Vitaran Polynukleotider",
          items: [
            { name: "Treatment course eyes (3x1ml)", price: "6,000,-" },
            { name: "Treatment course face (3x2ml)", price: "6,000,-" },
            { name: "Boost face or eyes", price: "2,200,-" }
          ]
        },
        {
          title: "Kamilane Shine",
          items: [
            { name: "Treatment course face (3x2ml)", price: "6,000,-" },
            { name: "Kamilane shine 2ml", price: "2,200,-" }
          ]
        }
      ]
    },
    {
      category: "Microneedling & PRP",
      description: "Consultation/pre-examination: free",
      subsections: [
        {
          title: "Skinpen Microneedling",
          items: [
            { name: "Skinpen face and neck", price: "1,500,-" },
            { name: "Skinpen with Purasomes (award-winning exosomes)", price: "2,000,-" },
            { name: "Package price 3 treatments with Skinpen", price: "3,600,-" },
            { name: "Package price 3 treatments with exosomes", price: "4,800,-" }
          ]
        },
        {
          title: "Package Deals Skinpen + Bio-stimulator",
          items: [
            { name: "Skinpen + Ejal40", price: "2,200,-" },
            { name: "Skinpen + Vitaran", price: "2,800,-" }
          ]
        },
        {
          title: "PRP/PRF Treatment",
          items: [
            { name: "1 Treatment, combined microneedling and injection in face incl. under eyes", price: "1,800,-" },
            { name: "1 Treatment, combined microneedling and injection, scalp (stimulates hair growth)", price: "1,800,-" },
            { name: "Package price 3 treatments face or scalp", price: "4,320,-" }
          ]
        },
        {
          title: "EXO-GOLD",
          items: [
            { name: "1 Treatment combined microneedling and injection in scalp with PRP and exosomes", price: "2,300,-" },
            { name: "Package price 3 treatments", price: "6,000,-" }
          ]
        }
      ]
    },
    {
      category: "Rejuvenate Packages",
      description: "Our most exclusive and complete rejuvenation packages",
      items: [
        {
          name: "Ultimate Rejuvenation 1",
          description: "Full face botox incl. neck bands, 4 ml filler, 2ml Kamilane Shine, 1ml Vitaran OR 1ml Sunekos Performa under eyes",
          price: "DKK 12,075,- (save 4,025,-)"
        },
        {
          name: "Ultimate Rejuvenation 2",
          description: "Full face botox, 3 ml filler, 2ml Kamilane Shine, 1ml Vitaran OR 1ml Sunekos Performa under eyes",
          price: "DKK 10,050,- (save 3,350,-)"
        },
        {
          name: "Maximum Rejuvenation 1",
          description: "3 areas botox incl. Nefertiti lift, 2ml filler, 2ml Ejal 40",
          price: "DKK 7,125,- (save 2,375,-)"
        },
        {
          name: "Maximum Rejuvenation 2",
          description: "3 areas botox, 1ml filler, 2ml Ejal40, 1ml Vitaran or 1ml Sunekos Performa under eyes",
          price: "DKK 6,150,- (save 2,050,-)"
        },
        {
          name: "Medium Rejuvenation",
          description: "2 areas botox, 2ml Ejal 40, 1ml Vitaran OR 1ml Sunekos Performa under eyes",
          price: "DKK 4,240,- (save 1,060,-)"
        },
        {
          name: "Mini Rejuvenation 1",
          description: "1 area botox, 1ml filler, 2ml Ejal40",
          price: "DKK 3,740,- (save 660,-)"
        },
        {
          name: "Mini Rejuvenation 2",
          description: "1 area area botox, 2ml Kamilane Shine OR 1ml Sunekos Performa OR 1ml Vitaran under eyes",
          price: "DKK 2,890,- (save 510,-)"
        },
        {
          name: "Rejuvenate Special",
          description: "1 PRF combined microneedling and injection in face incl. under eyes, 2 areas botox",
          price: "DKK 3,100,- (save 300,-)"
        }
      ]
    },
    {
      category: "Laser Hair Removal",
      description: "Permanent reduction of unwanted hair growth with the latest technology",
      subsections: [
        {
          title: "Women",
          items: [
            { name: "FULL-BODY LASER WOMEN", price: "2,000,-" },
            { name: "Full legs", price: "1,000,-" },
            { name: "Half legs", price: "700,-" },
            { name: "Neck (front)", price: "400,-" },
            { name: "Full face", price: "400,-" },
            { name: "Upper lip", price: "200,-" },
            { name: "Chin", price: "200,-" },
            { name: "Underarms", price: "400,-" },
            { name: "Full arms", price: "1,000,-" },
            { name: "Half arms", price: "700,-" },
            { name: "Chest", price: "700,-" },
            { name: "Stomach", price: "700,-" },
            { name: "Brazilian", price: "800,-" },
            { name: "Bikini line", price: "400,-" },
            { name: "Buttocks (between cheeks)", price: "400,-" },
            { name: "Buttocks", price: "600,-" },
            { name: "Feet", price: "200,-" },
            { name: "Shoulders", price: "700,-" },
            { name: "Full back", price: "1,200,-" },
            { name: "Lower back", price: "600,-" },
            { name: "Back of neck", price: "400,-" }
          ]
        },
        {
          title: "Package Deals Men",
          items: [
            { name: "Maximum Package (Full upper body)", price: "2,000,-" },
            { name: "Medium Package 1 (Back, shoulders, upper arms, and underarms)", price: "1,500,-" },
            { name: "Medium Package 2 (Chest, Stomach, Back)", price: "1,500,-" },
            { name: "Mini Package 1 (Back and shoulders)", price: "1,000,-" },
            { name: "Mini Package 2 (Chest and stomach)", price: "1,000,-" },
            { name: "Facial package (Cheeks, front and back of neck)", price: "800,-" }
          ]
        }
      ]
    },
    {
      category: "Private Doctor & Weight Loss",
      description: "Personal medical care and medical weight loss",
      subsections: [
        {
          title: "Medical Weight Loss",
          items: [
            { name: "Consultation, ONLINE, incl. prescription (medication is purchased separately at pharmacy)", price: "600,-" },
            { name: "Follow-up, ONLINE, incl. prescription (medication is purchased separately at pharmacy)", price: "300,-" },
            { name: "Off-label prescription *", price: "800,-" }
          ],
          notes: [
            "* BMI < 30 or BMI from 27 and absence of lifestyle diseases. BMI < 25 is not treated!"
          ]
        },
        {
          title: "Private Doctor Consultation & On-Call Service",
          items: [
            { name: "Consultation, online", price: "600,-" },
            { name: "Consultation, in-person", price: "800,- incl. services" },
            { name: "Medical certificate, illness work", price: "1,200,-" },
            { name: "Medical certificate, illness study", price: "600,-" },
            { name: "Health certificate, FRA", price: "500,- incl. services" }
          ],
          notes: [
            "Target group: Adults and children from 0-18 years.",
            "Reasons for consultation: Acute respiratory or urinary tract symptoms, acute abdominal pain, gynecological symptoms, acute skin rashes, skin conditions (acne, eczema, psoriasis, rosacea, melasma, seborrheic dermatitis, perioral dermatitis), gynecology (contraception, hormone therapy for menopausal women, IUD insertion/removal, contraceptive implant)."
          ]
        }
      ]
    }
  ]
};

const PriceList = () => {
  const { lang } = useLanguage();
  const isDa = lang === 'da';
  const categories = priceListData[lang];
  const [activeCategory, setActiveCategory] = React.useState(categories[0].category);

  const currentCategoryData = categories.find(cat => cat.category === activeCategory) || categories[0];

  const seoTitle = isDa 
    ? "Prisliste – Botox, Filler & Laser Behandlinger | Rejuvenate" 
    : "Price List – Botox, Filler & Laser Treatments | Rejuvenate";
  const seoDesc = isDa 
    ? "Se vores opdaterede priser på kosmetiske behandlinger, herunder Botox, filler, skinbooster og laserhårfjerning samt vores pakketilbud. Book tid online!" 
    : "View our updated prices for cosmetic treatments, including Botox, filler, skinbooster, laser hair removal, and package deals. Book your consultation!";

  const formatPrice = (price) => {
    if (!price) return '';
    const lowerPrice = price.toLowerCase();
    if (lowerPrice.includes('dkk')) {
      return price;
    }
    if (lowerPrice === 'gratis' || lowerPrice === 'free') {
      return price;
    }
    return `DKK ${price}`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="pt-16 md:pt-24 pb-24 bg-stone-50"
    >
      <SEO title={seoTitle} description={seoDesc} />
      <div className="max-w-7xl mx-auto px-6">
        <header className="text-center mb-16">
          <span className="text-orange-400 text-xs uppercase tracking-widest font-bold mb-4 block">
            {isDa ? 'Priser & Behandlinger' : 'Prices & Treatments'}
          </span>
          <h1 className="text-5xl font-serif mb-6">
            {isDa ? 'Prisliste' : 'Price List'}
          </h1>
          <p className="text-stone-500 max-w-2xl mx-auto">
            {isDa 
              ? 'Her finder du en komplet oversigt over vores behandlinger og priser. Alle kosmetiske behandlinger kræver en gratis forundersøgelse mindst 48 timer før.' 
              : 'Here you will find a complete overview of our treatments and prices. All cosmetic treatments require a free consultation at least 48 hours prior.'}
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-4 bg-white p-6 rounded-[32px] border border-stone-100 shadow-sm space-y-2 sticky top-28">
            <h3 className="text-xs uppercase tracking-widest font-bold text-stone-400 px-4 mb-4">
              {isDa ? 'Kategorier' : 'Categories'}
            </h3>
            <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-4 lg:pb-0 scrollbar-none">
              {categories.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => setActiveCategory(cat.category)}
                  className={cn(
                    "w-full text-left px-4 py-3.5 rounded-2xl text-sm font-medium transition-all whitespace-nowrap lg:whitespace-normal",
                    activeCategory === cat.category
                      ? "bg-stone-900 text-white shadow-md"
                      : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                  )}
                >
                  {cat.category}
                </button>
              ))}
            </div>
          </div>

          {/* Price Content */}
          <div className="lg:col-span-8 bg-white p-8 md:p-12 rounded-[40px] border border-stone-100 shadow-sm">
            <div className="mb-8 border-b border-stone-100 pb-6">
              <h2 className="text-3xl font-serif mb-2">{currentCategoryData.category}</h2>
              <p className="text-stone-400 text-xs uppercase tracking-widest font-medium">
                {currentCategoryData.description}
              </p>
            </div>

            <div className="space-y-10">
              {/* Direct Items */}
              {currentCategoryData.items && currentCategoryData.items.length > 0 && (
                <div className="space-y-4">
                  {currentCategoryData.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start gap-4 border-b border-stone-100 pb-4 last:border-0">
                      <div className="space-y-1">
                        <span className="text-stone-800 font-medium text-sm md:text-base">{item.name}</span>
                        {item.description && (
                          <p className="text-xs text-stone-500 leading-relaxed mt-1">{item.description}</p>
                        )}
                      </div>
                      <span className="text-stone-900 font-bold text-sm md:text-base whitespace-nowrap">
                        {formatPrice(item.price)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Subsections */}
              {currentCategoryData.subsections && currentCategoryData.subsections.map((sub, sIdx) => (
                <div key={sIdx} className="space-y-6 pt-4 first:pt-0">
                  <h3 className="text-lg font-serif border-b border-stone-200 pb-2 text-stone-900">
                    {sub.title}
                  </h3>
                  <div className="space-y-4">
                    {sub.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start gap-4 border-b border-stone-100 pb-4 last:border-0">
                        <div className="space-y-1">
                          <span className="text-stone-800 font-medium text-sm md:text-base">{item.name}</span>
                          {item.description && (
                            <p className="text-xs text-stone-500 leading-relaxed mt-1">{item.description}</p>
                          )}
                        </div>
                        <span className="text-stone-900 font-bold text-sm md:text-base whitespace-nowrap">
                          {formatPrice(item.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                  {sub.notes && (
                    <div className="bg-stone-50 p-6 rounded-2xl space-y-2 border border-stone-100 mt-4">
                      {sub.notes.map((note, nIdx) => (
                        <p key={nIdx} className="text-xs text-stone-500 leading-relaxed">
                          {note}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Category-level Notes */}
              {currentCategoryData.notes && (
                <div className="bg-stone-50 p-6 rounded-2xl space-y-2 border border-stone-100 mt-8">
                  {currentCategoryData.notes.map((note, idx) => (
                    <p key={idx} className="text-xs text-stone-500 leading-relaxed">
                      {note}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-12 pt-8 border-t border-stone-100 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="text-center sm:text-left">
                <h4 className="text-sm font-bold uppercase tracking-wider mb-1">
                  {isDa ? 'Klar til at bestille?' : 'Ready to book?'}
                </h4>
                <p className="text-xs text-stone-400">
                  {isDa ? 'Book din gratis forundersøgelse i dag.' : 'Book your free consultation today.'}
                </p>
              </div>
              <a
                href="https://www.eadministration.dk/kunde/kundelogin.asp?guid=E0AD3F34-9E76-4DE4-9C88-7D99EADF1148"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-stone-900 text-white px-8 py-4 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-stone-700 transition-colors whitespace-nowrap"
              >
                {isDa ? 'Book tid nu' : 'Book Now'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Academy = () => {
  const { lang } = useLanguage();
  const isDa = lang === 'da';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        alert(isDa ? 'Tak for din tilmelding! Vi kontakter dig hurtigst muligt.' : 'Thank you for registering! We will contact you as soon as possible.');
        e.target.reset();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="pt-16 md:pt-24 pb-24 bg-stone-50"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Hero Header */}
        <header className="text-center mb-16">
          <span className="text-orange-400 text-xs uppercase tracking-widest font-bold mb-4 block">
            {isDa ? 'Uddannelse & Registrering' : 'Education & Registration'}
          </span>
          <h1 className="text-5xl font-serif mb-6">
            {isDa ? 'Rejuvenate Akademi' : 'Rejuvenate Academy'}
          </h1>
          <p className="text-stone-500 max-w-3xl mx-auto leading-relaxed">
            {isDa 
              ? 'Rejuvenate tilbyder registreringskurser til læger med selvstændig virke, sygeplejersker og behandlere i Botox, Filler og Laserhårfjerning.' 
              : 'Rejuvenate offers registration courses for doctors, nurses, and practitioners in Botox, Filler, and Laser Hair Removal.'}
          </p>
        </header>

        {/* Video & Intro Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-24">
          <div className="lg:col-span-7 relative aspect-video rounded-[32px] overflow-hidden shadow-2xl bg-stone-900">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              src="https://assets.auxodev.dk/assets/9dae0889-d679-4ee4-8a71-4cd89c3be7b6/f86786aa-5863-4fd1-abad-00e8e0c75c8d.mov"
            />
          </div>
          <div className="lg:col-span-5 space-y-6">
            <h2 className="text-3xl font-serif text-stone-900">
              {isDa ? 'Hvorfor vælge os?' : 'Why choose us?'}
            </h2>
            <p className="text-stone-600 text-sm leading-relaxed">
              {isDa 
                ? 'Undervisning under supervision af speciallæge i almen medicin med 16 års erfaring + 4 års erfaring som kosmetisk læge med egen kosmetisk klinik. 1:1 supervision for at sikre optimal opnåelse af praktiske kompetencer i faget.'
                : 'Training under the supervision of a specialist in general medicine with 16 years of experience + 4 years of experience as a cosmetic doctor with their own cosmetic clinic. 1:1 supervision to ensure optimal practical skills.'}
            </p>
            <p className="text-stone-600 text-sm leading-relaxed">
              {isDa 
                ? 'Rejuvenate adskiller sig fra mange andre akademier ved, at du får meget mere hands-on træning især på botox og filler behandlinger. På filler-kurset er der garanti for at have udført mindst 1 læbe, hage, kindben, nasolabial fure, marionetlinjer, kæbelinje, teartrough og tindinger i din modelliste.'
                : 'Rejuvenate differs from many other academies by offering significantly more hands-on training, especially for Botox and filler treatments. On the filler course, you are guaranteed to have performed at least 1 lip, chin, cheekbone, nasolabial fold, marionette lines, jawline, tear trough, and temples on your model list.'}
            </p>
            <div className="bg-orange-50 border border-orange-100 p-6 rounded-2xl">
              <p className="text-xs text-orange-800 font-medium leading-relaxed">
                {isDa 
                  ? 'Uddannelsen foregår i Rejuvenates eksklusive lokaler i Rødovre.' 
                  : 'The training takes place at Rejuvenate\'s exclusive premises in Rødovre.'}
              </p>
            </div>
          </div>
        </div>

        {/* Klinikken Sikrer Section */}
        <div className="mb-24">
          <h2 className="text-3xl font-serif text-center mb-12">
            {isDa ? 'Klinikken sikrer dig:' : 'The clinic ensures:'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                num: "1",
                title: isDa ? "Modeller til praktisk træning" : "Models for practical training",
                desc: isDa 
                  ? "Vi skaffer modeller til din praktiske træning efter gældende kvalifikationskrav fra Sundhedsstyrelsen." 
                  : "We provide models for your practical training in accordance with current qualification requirements from the Danish Patient Safety Authority."
              },
              {
                num: "2",
                title: isDa ? "Teoretisk undervisning & kompendium" : "Theoretical teaching & compendium",
                desc: isDa 
                  ? "Teoretisk undervisning med powerpoint, whiteboard og et samlet kompendium, der indeholder basal anatomi, sikre injektionsteknikker, komplikationshåndtering (herunder protokoller for vaskulær okklusion, anafylaktisk shock, hjertestop), korrekt journalføring og lovgivning inden for kosmetiske behandlinger." 
                  : "Theoretical teaching with powerpoint, whiteboard, and a comprehensive compendium covering basic anatomy, safe injection techniques, complication management (including protocols for vascular occlusion, anaphylactic shock, cardiac arrest), correct record keeping, and legislation within cosmetic treatments."
              },
              {
                num: "3",
                title: isDa ? "Følgedag med lægen" : "Shadowing day with the doctor",
                desc: isDa 
                  ? "Hvor du foretager konsultationer på nye patienter under supervision, herunder formidling af patientinformation og journalføring. , jvf. Sundhedsstyrelsens krav for registrering." 
                  : "Where you perform consultations on new patients under supervision, including dissemination of patient information and record keeping, cf. the Danish Patient Safety Authority's requirements for registration."
              },
              {
                num: "4",
                title: isDa ? "Kursusbevis & modelliste" : "Certificate & model list",
                desc: isDa 
                  ? "Kursusbevis og modelliste til registrering i Sundhedsstyrelsen som kosmetisk behandler." 
                  : "Course certificate and model list for registration with the Danish Patient Safety Authority as a cosmetic practitioner."
              },
              {
                num: "5",
                title: isDa ? "Fuld forplejning" : "Full catering",
                desc: isDa 
                  ? "Fuld forplejning under hele kurset, så du kan fokusere 100% på din læring." 
                  : "Full catering throughout the course, so you can focus 100% on your learning."
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-8 rounded-3xl border border-stone-100 shadow-sm space-y-4">
                <span className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-sm">
                  {item.num}
                </span>
                <h3 className="text-lg font-serif text-stone-900">{item.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Priser Section */}
        <div className="mb-24">
          <h2 className="text-3xl font-serif text-center mb-12">
            {isDa ? 'Priser for Registreringskurser' : 'Registration Course Prices'}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Botox / Filler */}
            <div className="bg-white p-8 rounded-[32px] border border-stone-100 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-serif text-stone-900 mb-6 border-b border-stone-100 pb-4">
                  {isDa ? 'Botox & Filler' : 'Botox & Filler'}
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Sygeplejersker' : 'Nurses'}
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {isDa 
                        ? '20 superviserede filler behandlinger / 20 superviserede botox behandlinger inkl. teori og 1 følgedag. Varighed: 5 dage.' 
                        : '20 supervised filler treatments / 20 supervised botox treatments incl. theory and 1 shadowing day. Duration: 5 days.'}
                    </p>
                    <p className="text-lg font-bold text-stone-900 mt-2">DKK 75.000,-</p>
                  </div>
                  <div className="border-t border-stone-100 pt-4">
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Læger med selvstændigt virke' : 'Doctors with independent practice'}
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {isDa 
                        ? '50 superviserede botox behandlinger / 30 superviserede filler behandlinger inkl. teori og 1 følgedag. Varighed: 1-2 uger.' 
                        : '50 supervised botox treatments / 30 supervised filler treatments incl. theory and 1 shadowing day. Duration: 1-2 weeks.'}
                    </p>
                    <p className="text-lg font-bold text-stone-900 mt-2">DKK 135.000,-</p>
                  </div>
                  <div className="border-t border-stone-100 pt-4">
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Kosmetisk behandler (ikke sundhedsfaglig)' : 'Cosmetic practitioner (non-medical)'}
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {isDa 
                        ? '200 superviserede filler behandlinger. Varighed: 4-6 måneder.' 
                        : '200 supervised filler treatments. Duration: 4-6 months.'}
                    </p>
                    <p className="text-lg font-bold text-stone-900 mt-2">DKK 250.000,-</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Laser Hårfjerning */}
            <div className="bg-white p-8 rounded-[32px] border border-stone-100 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-serif text-stone-900 mb-6 border-b border-stone-100 pb-4">
                  {isDa ? 'Laserhårfjerning' : 'Laser Hair Removal'}
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Sygeplejersker' : 'Nurses'}
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {isDa 
                        ? '20 superviserede laser behandlinger inkl. teori. Varighed: 2 dage.' 
                        : '20 supervised laser treatments incl. theory. Duration: 2 days.'}
                    </p>
                    <p className="text-lg font-bold text-stone-900 mt-2">DKK 20.000,-</p>
                  </div>
                  <div className="border-t border-stone-100 pt-4">
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Kosmetisk behandler (ikke sundhedsfaglig)' : 'Cosmetic practitioner (non-medical)'}
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {isDa 
                        ? '20 superviserede behandlinger inkl. teori. Varighed: 2 dage.' 
                        : '20 supervised treatments incl. theory. Duration: 2 days.'}
                    </p>
                    <p className="text-lg font-bold text-stone-900 mt-2">DKK 20.000,-</p>
                  </div>
                  <div className="border-t border-stone-100 pt-4">
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Læger med selvstændigt virke' : 'Doctors with independent practice'}
                    </h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {isDa 
                        ? '30 superviserede laser behandlinger inkl. teori. I alt ca. 45 timer.' 
                        : '30 supervised laser treatments incl. theory. Total approx. 45 hours.'}
                    </p>
                    <p className="text-lg font-bold text-stone-900 mt-2">DKK 40.000,-</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pakkepriser */}
            <div className="bg-stone-900 text-white p-8 rounded-[32px] shadow-lg flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-serif text-white mb-6 border-b border-white/10 pb-4">
                  {isDa ? 'Pakkepriser (Alt-i-én)' : 'Package Prices (All-in-one)'}
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Sygeplejersker' : 'Nurses'}
                    </h4>
                    <p className="text-sm text-stone-300 leading-relaxed">
                      {isDa 
                        ? 'Komplet uddannelse i Botox, Filler og Laserhårfjerning.' 
                        : 'Complete training in Botox, Filler, and Laser Hair Removal.'}
                    </p>
                    <p className="text-2xl font-serif text-orange-400 mt-2">DKK 90.000,-</p>
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Læger med selvstændigt virke' : 'Doctors with independent practice'}
                    </h4>
                    <p className="text-sm text-stone-300 leading-relaxed">
                      {isDa 
                        ? 'Komplet uddannelse i Botox, Filler og Laserhårfjerning.' 
                        : 'Complete training in Botox, Filler, and Laser Hair Removal.'}
                    </p>
                    <p className="text-2xl font-serif text-orange-400 mt-2">DKK 170.000,-</p>
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <h4 className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-2">
                      {isDa ? 'Kosmetisk behandler (ikke sundhedsfaglig)' : 'Cosmetic practitioner (non-medical)'}
                    </h4>
                    <p className="text-sm text-stone-300 leading-relaxed">
                      {isDa 
                        ? 'Uddannelse i Filler og Laserhårfjerning (KUN filler og laser).' 
                        : 'Training in Filler and Laser Hair Removal (ONLY filler and laser).'}
                    </p>
                    <p className="text-2xl font-serif text-orange-400 mt-2">DKK 265.000,-</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tilmelding Form */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white p-12 md:p-20 rounded-[40px] shadow-sm border border-stone-100">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif mb-4">
                {isDa ? 'Tilmeld dig her' : 'Register Here'}
              </h2>
              <p className="text-stone-500 text-sm">
                {isDa 
                  ? 'Tilmelding sker ved at udfylde kontaktformularen nedenfor eller kontakte klinikken telefonisk. Kursusgrupper og datoer fastsættes løbende på efterspørgsel.' 
                  : 'Registration is done by filling out the contact form below or contacting the clinic by phone. Course groups and dates are set continuously on demand.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Honeypot */}
              <div className="sr-only absolute opacity-0">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Navn' : 'Name'}</label>
                  <input required name="name" type="text" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Telefon' : 'Phone'}</label>
                  <input required name="phone" type="tel" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors" />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">Email</label>
                <input required name="email" type="email" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors" />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Vælg Kursus' : 'Select Course'}</label>
                <select name="course" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors bg-transparent">
                  <option value="Botox & Filler">{isDa ? 'Botox & Filler' : 'Botox & Filler'}</option>
                  <option value="Laserhårfjerning">{isDa ? 'Laserhårfjerning' : 'Laser Hair Removal'}</option>
                  <option value="Pakkekursus (Alt-i-én)">{isDa ? 'Pakkekursus (Botox/Filler/Laser)' : 'Package Course (Botox/Filler/Laser)'}</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Uddannelsesbaggrund' : 'Educational Background'}</label>
                <select name="background" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors bg-transparent">
                  <option value="Sygeplejerske">{isDa ? 'Sygeplejerske' : 'Nurse'}</option>
                  <option value="Læge">{isDa ? 'Læge med selvstændigt virke' : 'Doctor with independent practice'}</option>
                  <option value="Kosmetisk behandler (ikke-sundhedsfaglig)">{isDa ? 'Kosmetisk behandler (ikke-sundhedsfaglig)' : 'Cosmetic practitioner (non-medical)'}</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{isDa ? 'Besked / Yderligere info' : 'Message / Additional Info'}</label>
                <textarea name="message" rows="4" className="w-full border-b border-stone-200 py-4 focus:border-orange-400 outline-none transition-colors resize-none"></textarea>
              </div>

              <button type="submit" className="w-full bg-stone-900 text-white py-6 rounded-2xl text-xs uppercase tracking-widest font-bold hover:bg-stone-700 transition-colors shadow-lg">
                {isDa ? 'Send tilmelding' : 'Submit Registration'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function App() {
  return (
    <LanguageProvider>
      <ScrollToTop />
      <div className="min-h-screen bg-white font-sans text-stone-900 selection:bg-orange-100 selection:text-orange-900">
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/behandlinger" element={<Treatments />} />
            <Route path="/behandlinger/:id" element={<TreatmentDetail />} />
            <Route path="/prisliste" element={<PriceList />} />
            <Route path="/galleri" element={<Gallery />} />
            <Route path="/privatlaege" element={<PrivateDoctor />} />
            <Route path="/akademi" element={<Academy />} />
            <Route path="/kontakt" element={<Contact />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </LanguageProvider>
  );
}
